import React, { useEffect, useRef, useState } from "react";

import {
  formatPeriodLabel,
  getPeriodPresets,
  matchesPeriodPreset,
} from "../model/calculations";
import { getBreakdownOptions } from "../model/defaults";
import { BreakdownKey, ReportConfiguration, ReportView } from "../model/types";

interface ReportViewSectionProps {
  view: ReportView;
  configuration: ReportConfiguration;
  availablePeriods: string[];
  filterOptions: Record<BreakdownKey, string[]> | null;
  onChange: (patch: Partial<ReportView>) => void;
}

/**
 * Section 2 - end user owned.
 *
 * Everything a report viewer can change. These controls only feed the `preview` block of
 * the generated configuration; they never touch the field mapping, so this component
 * reads dimension definitions only to render the end-user controls, and never modifies the
 * admin-owned configuration.
 */
export default function ReportViewSection(props: ReportViewSectionProps) {
  const { view, configuration, availablePeriods, filterOptions, onChange } =
    props;
  const breakdownOptions = getBreakdownOptions(configuration);
  // Computed fresh each render off the viewer's clock, not memoized - cheap, and a preset
  // should reflect "today" if the page is left open across midnight.
  const periodPresets = getPeriodPresets();
  const [filterQueries, setFilterQueries] = useState<
    Partial<Record<BreakdownKey, string>>
  >({});
  const sectionRef = useRef<HTMLElement>(null);

  const formatFilterValue = (key: BreakdownKey, value: string) =>
    key === "month" && value !== "No date" ? formatPeriodLabel(value) : value;

  const closeDropdown = (element: HTMLElement) => {
    element.closest("details")?.removeAttribute("open");
  };

  const closeOtherDropdowns = (current?: HTMLDetailsElement) => {
    sectionRef.current
      ?.querySelectorAll<HTMLDetailsElement>("details[open]")
      .forEach((dropdown) => {
        if (dropdown !== current) dropdown.removeAttribute("open");
      });
  };

  useEffect(() => {
    const closeOnOutsideClick = (event: PointerEvent) => {
      if (!sectionRef.current?.contains(event.target as Node)) {
        closeOtherDropdowns();
      }
    };

    document.addEventListener("pointerdown", closeOnOutsideClick);
    return () =>
      document.removeEventListener("pointerdown", closeOnOutsideClick);
  }, []);

  const toggleFilter = (key: BreakdownKey, value: string) => {
    const selected = view.filters[key] ?? [];
    onChange({
      filters: {
        ...view.filters,
        [key]: selected.includes(value)
          ? selected.filter((item) => item !== value)
          : [...selected, value],
      },
    });
  };

  const setFilter = (key: BreakdownKey, values: string[]) => {
    onChange({ filters: { ...view.filters, [key]: values } });
  };

  const togglePeriod = (period: string) => {
    onChange({
      periods: view.periods.includes(period)
        ? view.periods.filter((item) => item !== period)
        : [...view.periods, period],
    });
  };

  return (
    <section
      ref={sectionRef}
      className="finance-reporting-group finance-reporting-group--report"
      aria-labelledby="finance-reporting-view-title"
      onPointerDownCapture={(event) => {
        if (!(event.target as HTMLElement).closest("details")) {
          closeOtherDropdowns();
        }
      }}
    >
      <header className="finance-reporting-group__heading">
        <h3
          className="finance-reporting-group__title"
          id="finance-reporting-view-title"
        >
          Report view
        </h3>
      </header>

      <div className="finance-reporting-section">
        <div className="finance-reporting-section__heading">
          <span className="finance-reporting-section__number">01</span>
          <div>
            <h4>Period selection</h4>
            <p>Reporting months available in the source data.</p>
          </div>
        </div>
        <details
          className="finance-reporting-filter finance-reporting-filter--period"
          onToggle={(event) => {
            if (event.currentTarget.open) {
              closeOtherDropdowns(event.currentTarget);
            }
          }}
        >
          <summary>
            <span>Months</span>
            <strong>
              {view.periods.length
                ? `${view.periods.length} selected`
                : "All dates"}
            </strong>
          </summary>
          <div className="finance-reporting-filter__menu">
            <div className="finance-reporting-filter__actions">
              <button
                type="button"
                disabled={view.periods.length === 0}
                onClick={(event) => {
                  onChange({ periods: [] });
                  closeDropdown(event.currentTarget);
                }}
              >
                Clear
              </button>
            </div>
            <div className="finance-reporting-filter__presets">
              {periodPresets.map((preset) => (
                <button
                  key={preset.key}
                  type="button"
                  className={`finance-reporting-filter__preset${
                    matchesPeriodPreset(view.periods, preset)
                      ? " finance-reporting-filter__preset--active"
                      : ""
                  }`}
                  onClick={(event) => {
                    onChange({ periods: preset.periods });
                    closeDropdown(event.currentTarget);
                  }}
                >
                  {preset.label}
                </button>
              ))}
            </div>
            {availablePeriods.map((period) => (
              <label key={period}>
                <input
                  type="checkbox"
                  checked={view.periods.includes(period)}
                  onChange={() => {
                    togglePeriod(period);
                  }}
                />
                <span>{formatPeriodLabel(period)}</span>
              </label>
            ))}
          </div>
        </details>
      </div>

      <div className="finance-reporting-section">
        <div className="finance-reporting-section__heading">
          <span className="finance-reporting-section__number">02</span>
          <div>
            <h4>Filters</h4>
            <p>Narrow the report using one or more values.</p>
          </div>
        </div>
        <div className="finance-reporting-filters">
          {breakdownOptions.map((option) => {
            const selected = view.filters[option.key] ?? [];
            const options = filterOptions?.[option.key] ?? [];
            const query = filterQueries[option.key] ?? "";
            const visibleOptions = options.filter((value) =>
              formatFilterValue(option.key, value)
                .toLocaleLowerCase()
                .includes(query.trim().toLocaleLowerCase()),
            );

            return (
              <details
                className="finance-reporting-filter"
                key={option.key}
                onToggle={(event) => {
                  if (event.currentTarget.open) {
                    closeOtherDropdowns(event.currentTarget);
                  }
                }}
              >
                <summary>
                  <span>{option.label}</span>
                  <strong>
                    {selected.length ? `${selected.length} selected` : "All"}
                  </strong>
                </summary>
                <div className="finance-reporting-filter__menu">
                  <div className="finance-reporting-filter__actions">
                    <input
                      type="search"
                      className="finance-reporting-filter__search"
                      value={query}
                      placeholder="Filter values"
                      aria-label={`Filter ${option.label} values`}
                      onChange={(event) =>
                        setFilterQueries((current) => ({
                          ...current,
                          [option.key]: event.target.value,
                        }))
                      }
                    />
                    <button
                      type="button"
                      disabled={selected.length === 0}
                      onClick={(event) => {
                        setFilter(option.key, []);
                        closeDropdown(event.currentTarget);
                      }}
                    >
                      Clear
                    </button>
                  </div>
                  {visibleOptions.map((value) => (
                    <label key={value}>
                      <input
                        type="checkbox"
                        checked={selected.includes(value)}
                        onChange={() => {
                          toggleFilter(option.key, value);
                        }}
                      />
                      <span>{formatFilterValue(option.key, value)}</span>
                    </label>
                  ))}
                  {visibleOptions.length === 0 && (
                    <span className="finance-reporting-filter__empty">
                      No matching values
                    </span>
                  )}
                </div>
              </details>
            );
          })}
        </div>
      </div>

      <div className="finance-reporting-section">
        <div className="finance-reporting-section__heading">
          <span className="finance-reporting-section__number">03</span>
          <div>
            <h4>Breakdown by</h4>
            <p>Choose how report results are grouped.</p>
          </div>
        </div>
        <label className="finance-reporting-field">
          <select
            className="finance-reporting-field__control finance-reporting-field__control--compact"
            value={view.breakdown}
            onChange={(event) =>
              onChange({ breakdown: event.target.value as BreakdownKey })
            }
          >
            {breakdownOptions.map((option) => (
              <option key={option.key} value={option.key}>
                {option.label}
              </option>
            ))}
          </select>
        </label>
      </div>
    </section>
  );
}
